import { ReactNode, useEffect, useState } from "react";
import {
    FooterColumnContainer,
    FooterColumnContent,
    PageButton,
    PageStatus,
    Pagination,
    TBody,
    THead,
    TableStyle,
    Th,
    LoadingColumnContent,
} from "./Table.styles";
import Spinner from "../spinner/Spinner";
import TableRow from "./components/TableRow/TableRow";
import _ from "lodash";
import { TiArrowSortedDown, TiArrowSortedUp } from "react-icons/ti";
import { FaSort } from "react-icons/fa";

/**
 * Represents a column in the table.
 * @interface Column
 * @property {string} key - The key for the column.
 * @property {(columnData: any, row: any) => ReactNode} render - The render function for the column.
 * @property {string} label - The label for the column.
 */
export interface Column {
    key: string;
    render?: (columnData: any, row: any) => ReactNode;
    label: string;
    // Only the columns a table actually sorts on supply one; the header guards
    // on it before sorting.
    accessor?: string;
    sort?: boolean;
}

/**
 * Props for the Table component.
 * @interface TableProps
 * @property {any[]} data - The data to be displayed in the table.
 * @property {Column[]} [columns] - The columns to be displayed in the table. If not provided, the keys of the first item in the data array will be used as column labels.
 * @property {string} [noRowsText] - The text to be displayed when there are no rows in the table.
 */
export interface TableProps {
    data: any[];
    columns?: Column[];
    noRowsText?: string;
    // An empty table means one of two things, and only the caller knows which:
    // the request is still out, or it came back empty. While `loading` is set
    // the table says nothing about the rows and shows the caption instead.
    loading?: boolean;
    loadingText?: string;
    enableReviewModal?: boolean;
    // Rows per page. Omit to show every row and no pager. When set, `data` must
    // be a stable reference (memoise it) — the table returns to the first page
    // whenever it changes, which is what a new search should do, but an array
    // rebuilt on every render would pin the table to page one.
    pageSize?: number;
    previousLabel?: string;
    nextLabel?: string;
}

/**
 * A customizable table component.
 * @param {TableProps} props - The props for the Table component.
 * @returns {JSX.Element} - The rendered Table component.
 */
export default function Table({
    data,
    columns,
    noRowsText = "No data to display",
    loading = false,
    loadingText = "Loading...",
    enableReviewModal,
    pageSize,
    previousLabel = "Previous",
    nextLabel = "Next",
}: TableProps): JSX.Element {
    // Constants for the sort order.
    const ASCENDING_ORDER = "asc";
    const DESCENDING_ORDER = "desc";

    // State for the sort configuration.
    const [sortConfig, setSortConfig] = useState({
        key: "",
        direction: ASCENDING_ORDER,
    });

    // Which page of rows is on screen (0-indexed). Only used when `pageSize` is set.
    const [page, setPage] = useState(0);

    // Narrowing the rows (a search) can strand the user on a page that no longer
    // exists, so start again from the first page whenever the data changes.
    useEffect(() => {
        setPage(0);
    }, [data]);

    // The columns for the table.
    const tableColumns =
        columns ||
        ((data.length
            ? Object.keys(data[0]).map((key) => ({
                  key,
                  label: key,
              }))
            : []) as Column[]);

    // Function to sort the data.
    const sortData = (key: string) => {
        let direction = ASCENDING_ORDER;
        if (
            sortConfig.key === key &&
            sortConfig.direction === ASCENDING_ORDER
        ) {
            direction = DESCENDING_ORDER;
        }
        setSortConfig({ key, direction });
        // A re-sort reorders everything, so the page you were on no longer means
        // anything — show the top of the new order.
        setPage(0);
    };

    // Sort the data with the current sort configuration.
    const sortedData = _.orderBy(
        data,
        [sortConfig.key],
        [
            sortConfig.direction === ASCENDING_ORDER
                ? ASCENDING_ORDER
                : DESCENDING_ORDER,
        ],
    );

    // Sorting runs across the whole set before paging, so the first page holds
    // the first rows of the sorted order — not a re-sorted page one.
    const pageCount = pageSize
        ? Math.max(1, Math.ceil(sortedData.length / pageSize))
        : 1;
    // Clamp rather than trust `page`: the row count can shrink under us.
    const currentPage = Math.min(page, pageCount - 1);
    const visibleData = pageSize
        ? sortedData.slice(
              currentPage * pageSize,
              currentPage * pageSize + pageSize,
          )
        : sortedData;

    return (
        <>
            <TableStyle>
                <THead>
                    <tr>
                        {tableColumns.map((column, index) => (
                            <Th
                                key={column.accessor || index}
                                scope="col"
                                sortable={Boolean(column.accessor)}
                                onClick={() =>
                                    column.accessor && sortData(column.accessor)
                                }
                            >
                                <div>
                                    {column.label}
                                    {column.sort &&
                                        sortConfig.key !== column.accessor && (
                                            <FaSort />
                                        )}

                                    {column.accessor &&
                                        sortConfig.key === column.accessor &&
                                        (sortConfig.direction ===
                                        ASCENDING_ORDER ? (
                                            <TiArrowSortedUp />
                                        ) : (
                                            <TiArrowSortedDown />
                                        ))}
                                </div>
                            </Th>
                        ))}
                    </tr>
                </THead>
                <TBody>
                    {visibleData.map((item, rowIndex) => (
                        <TableRow
                            key={rowIndex}
                            item={item}
                            tableColumns={tableColumns}
                            enableReviewModal={enableReviewModal}
                        />
                    ))}
                </TBody>
                {data.length ? null : (
                    <tfoot>
                        <tr>
                            <td
                                colSpan={tableColumns.length}
                                className="p-4 bg-gray-50"
                            >
                                <FooterColumnContainer>
                                    {loading ? (
                                        <LoadingColumnContent>
                                            <Spinner size="2" />
                                            {loadingText}
                                        </LoadingColumnContent>
                                    ) : (
                                        <FooterColumnContent>
                                            {noRowsText}
                                        </FooterColumnContent>
                                    )}
                                </FooterColumnContainer>
                            </td>
                        </tr>
                    </tfoot>
                )}
            </TableStyle>
            {pageSize !== undefined && pageCount > 1 && (
                <Pagination aria-label="Pagination">
                    <PageButton
                        type="button"
                        onClick={() => setPage(currentPage - 1)}
                        disabled={currentPage === 0}
                    >
                        {previousLabel}
                    </PageButton>
                    {/* Digits only, so it reads the same in every language. */}
                    <PageStatus>
                        {currentPage + 1} / {pageCount}
                    </PageStatus>
                    <PageButton
                        type="button"
                        onClick={() => setPage(currentPage + 1)}
                        disabled={currentPage >= pageCount - 1}
                    >
                        {nextLabel}
                    </PageButton>
                </Pagination>
            )}
        </>
    );
}
